import type { ComputedRef } from 'vue'
import { computed } from 'vue'
import { topmostOverlayPanelZ } from './useOverlayStack'

export const FLOATING_Z_OFFSET_IN_OVERLAY = 5

/** Fallback z-indexes when floating UI is rendered outside any overlay. Mirror
 *  the `--z-index-popover` / `--z-index-tooltip` tokens in `theme.css`. */
export const FLOATING_Z_POPOVER = 1000
export const FLOATING_Z_TOOLTIP = 1100

export interface UseFloatingZOptions {
  /** Z-index used when this element is NOT inside an overlay. */
  fallback?: number
  /** Extra offset above the topmost overlay panel. */
  offset?: number
}

/**
 * Compute the z-index for a floating UI element (Select, Combobox, Popover,
 * Tooltip, HoverCard) so that it always renders above whatever it belongs to:
 *
 *   - With at least one overlay open: topmost overlay panel z + small offset
 *   - Outside any overlay: the static `fallback` (popover/tooltip token)
 *
 * Why a global topmost lookup (not provide/inject): floating UI is usually
 * passed as SLOT content inside a Sheet/Dialog (e.g. a Select inside a
 * confirmation modal). In Vue 3, slot content inherits provides from the
 * scope OWNER (the feature component), not from the component rendering
 * `<slot>` (DialogContent). So `inject(OVERLAY_PANEL_Z_KEY)` from inside
 * SelectContent would miss the modal's provide entirely and fall through to
 * the popover fallback (1000), placing the dropdown UNDER the modal at
 * z-2010. Reading the global `topmostOverlayPanelZ` instead works regardless
 * of where in the tree the floating element lives.
 *
 * Tradeoff: when two non-modal slideovers are open simultaneously and the
 * user opens a Select inside the background one, the dropdown still renders
 * above the topmost slideover. In practice this is rare (selects close on
 * outside click) and acceptable.
 */
export function useFloatingZ(options: UseFloatingZOptions = {}): ComputedRef<number> {
  const { fallback = FLOATING_Z_POPOVER, offset = FLOATING_Z_OFFSET_IN_OVERLAY } = options
  return computed(() => {
    const top = topmostOverlayPanelZ.value
    return top != null ? top + offset : fallback
  })
}
